import React, { useEffect, useId, useRef } from "react"
import {
  Button,
  ColorPicker,
  Drawer,
  InputNumber,
  Segmented,
  Select,
  Switch,
} from "antd"
import {
  DEFAULT_TOTAL_SHADE,
  DEFAULT_ZEBRA,
  effectiveTweaks,
  LOOK_PRESETS,
} from "shared/ui/Table/style/looks"
import { EXCEL_DEFAULTS, SIG_DEFAULTS } from "shared/ui/Table/style/presets"
import {
  FONT_OPTIONS,
  isCustomised,
  LinesMode,
  LINES_MODES,
  PRESET_IDS,
  StyleTweaks,
  TableStyleChoice,
  withTweak,
} from "types/tableStyle.types"

/** Props of the Customise drawer; the caller holds the value and saves each change. */
export interface TableStyleDrawerProps {
  /** Whether the drawer is shown; Cancel goes back to the value held when it opened. */
  open: boolean
  /** Names the scope: "Table style: report default", "Table style: tab 'Q5'", "Table style: Query Console". */
  title: string
  /** The choice being edited; undefined shows `fallback` (an inheriting tab shows the report's). */
  value: TableStyleChoice | undefined
  /** The choice shown and edited from while `value` is undefined. */
  fallback: TableStyleChoice
  /** Called on every change; the first change of an undefined value creates a choice. */
  onChange: (value: TableStyleChoice | undefined) => void
  /** Called by Done, Cancel and the close button; the caller sets `open` to false. */
  onClose: () => void
}

/** An antd colour or CSS hex as "#rrggbb" (lower case, alpha dropped, short forms expanded). */
export const colorToHex = (
  color: string | { toHexString: () => string }
): string => {
  const raw = (typeof color === "string" ? color : color.toHexString())
    .trim()
    .toLowerCase()
  const match = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(raw)
  if (!match) return "#000000"
  const digits =
    match[1].length <= 4
      ? match[1]
          .split("")
          .map((d) => d + d)
          .join("")
      : match[1]
  return `#${digits.slice(0, 6)}`
}

/** A small colour chip (a look's banner fill) shown before its name. */
export const Swatch = ({ color }: { color: string }) => (
  <span
    aria-hidden
    className="inline-block align-middle mr-1.5 rounded-sm"
    style={{ width: 14, height: 10, background: color, border: "1px solid #bfbfbf" }}
  />
)

const FONT_SIZES = Array.from({ length: 17 }, (_, i) => 8 + i * 0.5) // 8 to 16
const DECIMALS = [0, 1, 2, 3, 4]
const ENGINE = "engine"
const LINES_LABELS: Record<LinesMode, string> = {
  grid: "Grid",
  rows: "Rows",
  rules: "Rules",
  none: "None",
}

const FONT_SELECT_OPTIONS = FONT_OPTIONS.map((font) => ({
  value: font.id,
  label: font.label,
}))
const FONT_SIZE_OPTIONS = FONT_SIZES.map((size) => ({
  value: size,
  label: String(size),
}))
const DECIMAL_OPTIONS: { value: number | typeof ENGINE; label: string }[] = [
  { value: ENGINE, label: "Engine" },
  ...DECIMALS.map((n) => ({ value: n, label: String(n) })),
]
const LINES_OPTIONS = LINES_MODES.map((mode) => ({
  label: LINES_LABELS[mode],
  value: mode,
}))
const ALIGN_OPTIONS: { label: string; value: "left" | "center" }[] = [
  { label: "Left", value: "left" },
  { label: "Centre", value: "center" },
]

/**
 * Colours set from a picker without a clear button (zebra and Total shade
 * are turned off by their switch instead).
 */
type ColorKey =
  | "textColor"
  | "bannerText"
  | "subBannerText"
  | "rowLabelText"
  | "lineColor"
  | "zebra"
  | "totalShade"
  | "sigFill1"
  | "sigFill2"
  | "arrowUpColor"
  | "arrowDownColor"

/** Fills that also take `null` (no fill). */
type FillKey = "bannerFill" | "subBannerFill" | "rowLabelFill"

type DecimalsKey = "percentDecimals" | "meanDecimals"
type WidthKey = "excelStubWidth" | "excelDataWidth"

const Section = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <section className="border-t border-gray-200 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
      {title}
    </h4>
    {children}
  </section>
)

const Row = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="flex items-center gap-2 my-1 flex-wrap">
    <span className="w-28 shrink-0 text-xs text-gray-600">{label}</span>
    {children}
  </div>
)

interface ColorFieldProps {
  label: string
  /** "#rrggbb"; `null` is no fill ("None"), undefined is unset ("Auto"). */
  value: string | null | undefined
  /** Offers the picker's clear button, which sets `null`. */
  nullable?: boolean
  onChange: (value: string | null) => void
}

/**
 * A hex colour picker, committed on `onChangeComplete` so dragging does not
 * flood state. Clearing also completes a transparent copy of the colour
 * before `onClear`; that one is skipped, so a clear emits `null` only.
 *
 * Alpha is disabled only while a colour is set: antd warns when alpha is
 * disabled on a transparent colour, which an unset or cleared one is. A
 * colour picked from a cleared one is made opaque by the panel, and
 * `colorToHex` drops alpha in any case.
 */
const ColorField = ({ label, value, nullable = false, onChange }: ColorFieldProps) => (
  <span role="group" aria-label={label} className="inline-flex">
    <ColorPicker
      size="small"
      format="hex"
      disabledAlpha={Boolean(value)}
      value={value ?? undefined}
      showText={() => (value ? value : value === null ? "None" : "Auto")}
      allowClear={nullable}
      onClear={nullable ? () => onChange(null) : undefined}
      onChangeComplete={(color) => {
        if (!color.cleared) onChange(colorToHex(color))
      }}
    />
  </span>
)

/**
 * Customise drawer: starts from a preset and changes single tweaks on top of
 * it. Each control shows the effective value (the preset's merged with the
 * user's) and a change writes only its own field, so a later improvement to
 * a preset still reaches the fields the user left alone.
 *
 * The drawer has no mask: the tables behind it stay visible as the live
 * preview. Cancel restores the value held when the drawer opened (including
 * `undefined` for an inheriting tab); Done and the close button keep the
 * changes.
 */
const TableStyleDrawer: React.FC<TableStyleDrawerProps> = ({
  open,
  title,
  value,
  fallback,
  onChange,
  onClose,
}) => {
  const alignName = useId()
  const linesName = useId()
  // The value when the drawer last opened, for Cancel
  const openedWith = useRef(value)
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) openedWith.current = value
    wasOpen.current = open
  }, [open, value])

  const shown = value ?? fallback
  const eff = effectiveTweaks(shown)

  const set = <K extends keyof StyleTweaks>(
    key: K,
    v: StyleTweaks[K] | undefined
  ): void => onChange(withTweak(shown, key, v))

  const cancel = (): void => {
    if (JSON.stringify(openedWith.current) !== JSON.stringify(value)) {
      onChange(openedWith.current)
    }
    onClose()
  }

  const colorField = (label: string, key: ColorKey, shownValue = eff[key]) => (
    <ColorField
      label={label}
      value={shownValue}
      onChange={(v) => {
        if (v !== null) set(key, v)
      }}
    />
  )

  const fillField = (label: string, key: FillKey) => (
    <ColorField
      label={label}
      value={eff[key]}
      nullable
      onChange={(v) => set(key, v)}
    />
  )

  const decimalsSelect = (label: string, key: DecimalsKey) => (
    <Select
      size="small"
      aria-label={label}
      className="w-24"
      value={eff[key] ?? ENGINE}
      options={DECIMAL_OPTIONS}
      onChange={(v: number | typeof ENGINE) =>
        set(key, typeof v === "number" ? v : undefined)
      }
    />
  )

  // Typing emits each whole number in range. A decimal or out-of-range entry
  // is not emitted; the input rounds or clamps it on blur and emits that.
  const widthInput = (label: string, key: WidthKey, defaultWidth: number) => (
    <InputNumber
      size="small"
      aria-label={label}
      className="w-20"
      min={4}
      max={100}
      precision={0}
      value={eff[key] ?? defaultWidth}
      onChange={(v) => {
        if (typeof v === "number" && Number.isInteger(v)) set(key, v)
      }}
    />
  )

  const shadeToggle = (
    label: string,
    key: "zebra" | "totalShade",
    colorLabel: string,
    defaultColor: string
  ) => (
    <Row label={label}>
      <Switch
        size="small"
        aria-label={label}
        checked={Boolean(eff[key])}
        onChange={(on) => set(key, on ? eff[key] || defaultColor : null)}
      />
      {eff[key] ? colorField(colorLabel, key) : null}
    </Row>
  )

  return (
    <Drawer
      open={open}
      title={title}
      placement="right"
      width={340}
      mask={false}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            size="small"
            disabled={!isCustomised(value)}
            onClick={() => onChange({ preset: shown.preset })}
          >
            Reset to preset
          </Button>
          <span className="flex-1" />
          <Button size="small" onClick={cancel}>
            Cancel
          </Button>
          <Button size="small" type="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      }
    >
      <Section title="Start from">
        <div
          role="radiogroup"
          aria-label="Start from preset"
          className="grid grid-cols-2 gap-2"
        >
          {PRESET_IDS.map((id) => {
            const checked = shown.preset === id
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={checked}
                className={`flex items-center px-2 py-1.5 rounded border text-xs text-left cursor-pointer ${
                  checked
                    ? "border-[var(--dq-brand)] bg-[var(--dq-brand-soft)]"
                    : "border-gray-300 bg-white hover:border-gray-400"
                }`}
                onClick={() => onChange({ preset: id })}
              >
                <Swatch color={LOOK_PRESETS[id].swatch} />
                {LOOK_PRESETS[id].label}
              </button>
            )
          })}
        </div>
      </Section>

      <Section title="Text">
        <Row label="Font">
          <Select
            size="small"
            aria-label="Font"
            className="w-40"
            value={eff.fontFamily ?? "app"}
            options={FONT_SELECT_OPTIONS}
            onChange={(v: string) => set("fontFamily", v)}
          />
        </Row>
        <Row label="Font size">
          <Select
            size="small"
            aria-label="Font size"
            className="w-20"
            value={eff.fontSizePt ?? 10.5}
            options={FONT_SIZE_OPTIONS}
            onChange={(v: number) => set("fontSizePt", v)}
          />
        </Row>
        <Row label="Text colour">{colorField("Text colour", "textColor")}</Row>
      </Section>

      <Section title="Colours">
        <Row label="Banner fill">{fillField("Banner fill", "bannerFill")}</Row>
        <Row label="Banner text">{colorField("Banner text", "bannerText")}</Row>
        <Row label="Sub-banner fill">
          {fillField("Sub-banner fill", "subBannerFill")}
        </Row>
        <Row label="Sub-banner text">
          {colorField("Sub-banner text", "subBannerText")}
        </Row>
        <Row label="Row label fill">
          {fillField("Row label fill", "rowLabelFill")}
        </Row>
        <Row label="Row label text">
          {colorField("Row label text", "rowLabelText")}
        </Row>
        <Row label="Row label alignment">
          <Segmented<"left" | "center">
            size="small"
            aria-label="Row label alignment"
            name={alignName}
            options={ALIGN_OPTIONS}
            value={eff.rowLabelAlign ?? "left"}
            onChange={(v) => set("rowLabelAlign", v)}
          />
        </Row>
      </Section>

      <Section title="Lines and bands">
        <Row label="Lines">
          <Segmented<LinesMode>
            size="small"
            aria-label="Lines"
            name={linesName}
            options={LINES_OPTIONS}
            value={eff.lines ?? "grid"}
            onChange={(v) => set("lines", v)}
          />
        </Row>
        <Row label="Line colour">{colorField("Line colour", "lineColor")}</Row>
        {shadeToggle("Zebra rows", "zebra", "Zebra colour", DEFAULT_ZEBRA)}
        {shadeToggle(
          "Shade Total column",
          "totalShade",
          "Total shade colour",
          DEFAULT_TOTAL_SHADE
        )}
      </Section>

      <Section title="Significance">
        <Row label="Higher, level 1">
          {colorField(
            "Higher, level 1",
            "sigFill1",
            eff.sigFill1 ?? SIG_DEFAULTS.upper.fill
          )}
        </Row>
        <Row label="Higher, level 2">
          {colorField(
            "Higher, level 2",
            "sigFill2",
            eff.sigFill2 ?? SIG_DEFAULTS.lower.fill
          )}
        </Row>
        <Row label="Arrow up">
          {colorField(
            "Arrow up",
            "arrowUpColor",
            eff.arrowUpColor ?? SIG_DEFAULTS.arrowUp.color
          )}
        </Row>
        <Row label="Arrow down">
          {colorField(
            "Arrow down",
            "arrowDownColor",
            eff.arrowDownColor ?? SIG_DEFAULTS.arrowDown.color
          )}
        </Row>
      </Section>

      <Section title="Numbers and Excel">
        <Row label="Percent decimals">
          {decimalsSelect("Percent decimals", "percentDecimals")}
        </Row>
        <Row label="Mean decimals">
          {decimalsSelect("Mean decimals", "meanDecimals")}
        </Row>
        <Row label="Label column width">
          {widthInput(
            "Label column width",
            "excelStubWidth",
            EXCEL_DEFAULTS.stubWidth
          )}
        </Row>
        <Row label="Data column width">
          {widthInput(
            "Data column width",
            "excelDataWidth",
            EXCEL_DEFAULTS.dataWidth
          )}
        </Row>
        <Row label="Freeze panes">
          <Switch
            size="small"
            aria-label="Freeze panes"
            checked={eff.freezePanes ?? EXCEL_DEFAULTS.freezePanes}
            onChange={(checked) => set("freezePanes", checked)}
          />
        </Row>
      </Section>
    </Drawer>
  )
}

export default TableStyleDrawer
